// ✅ Fixed Version: Professional Animated Error Page with Bead Garland + Ripple Effect
// Update: Ripple color now matches the on-screen bead palette (purple/green/amber).
// The ripple color is picked from the same 3-bead palette based on click position.

import React, { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

// NOTE: If you get "Cannot find module 'framer-motion'" error:
// Run: npm install framer-motion @types/framer-motion 


const BEAD_COLORS = ["#8b5cf6", "#10b981", "#f59e0b"]; // purple, green, amber

const BeadBackdrop = () => (
  <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-rose-50 via-white to-white" />
    {["#f59e0b", "#8b5cf6", "#10b981"].map((hex, i) => (
      <motion.div
        key={hex}
        animate={{ y: [0, -8, 0], opacity: [0.8, 1, 0.8] }}
        transition={{ duration: 6 + i * 0.4, delay: i * 0.15, repeat: Infinity, ease: [0.42, 0, 0.58, 1] }}
        className="absolute h-36 w-36 rounded-full blur-3xl opacity-30"
        style={{
          left: `${15 + i * 30}%`,
          top: `${14 + i * 20}%`,
          background: `radial-gradient(circle at 30% 30%, ${hex}33, transparent 60%)`,
        }}
      />
    ))}
  </div>
);

const BeadGarland: React.FC = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const swayX = useSpring(useTransform(mx, (v: number) => v * 8), { stiffness: 140, damping: 18 });
  const swayY = useSpring(useTransform(my, (v: number) => v * 8), { stiffness: 140, damping: 18 });

  return (
    <motion.svg
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      viewBox="0 0 520 200"
      className="mx-auto block h-40 w-full"
    >
      <motion.g style={{ x: swayX, y: swayY }}>
        <path id="garland" d="M10,60 C160,10 360,110 510,60" fill="none" stroke="rgba(203,213,225,0.7)" strokeWidth="3" strokeLinecap="round" />
        {[0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72, 0.84, 0.96].map((offset, i) => (
          <motion.circle
            key={i}
            r={12 + (i % 3) * 2}
            fill={i % 3 === 0 ? BEAD_COLORS[0] : i % 3 === 1 ? BEAD_COLORS[1] : BEAD_COLORS[2]}
            whileHover={{ scale: 1.2, filter: "brightness(1.1)" }}
            whileTap={{ scale: 0.95 }}
          >
            <animateMotion
              dur={`${5 + i * 0.3}s`}
              repeatCount="indefinite"
              keyPoints={`${offset};${offset + 0.02};${offset}`}
              keyTimes="0;0.5;1"
              calcMode="linear"
            >
              <mpath href="#garland" />
            </animateMotion>
            <title>Interactive bead</title>
          </motion.circle>
        ))}
      </motion.g>
    </motion.svg>
  );
};

const Ripple: React.FC<{ x: number; y: number; id: number; color: string; onComplete: (id: number) => void }> = ({ x, y, id, color, onComplete }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0.6 }}
    animate={{ scale: 4, opacity: 0 }}
    transition={{ duration: 1, ease: [0.42, 0, 0.58, 1] }}
    onAnimationComplete={() => onComplete(id)}
    className="absolute rounded-full border-2"
    style={{
      left: x - 50,
      top: y - 50,
      width: 100,
      height: 100,
      borderColor: color,
      boxShadow: `0 0 0 2px ${color}20`,
      pointerEvents: "none",
    }}
  />
);

const ErrorPageLight: React.FC = () => {
  const [ripples, setRipples] = useState<{ id: number; x: number; y: number; color: string }[]>([]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();

    // Map click position to bead color index so ripples mirror the on-screen bead palette.
    // Left third -> purple, middle third -> green, right third -> amber.
    const rel = x / rect.width;
    const idx = Math.min(BEAD_COLORS.length - 1, Math.floor(rel * BEAD_COLORS.length));
    const color = BEAD_COLORS[idx];

    setRipples((prev) => [...prev, { id, x, y, color }]);
  };

  const removeRipple = (id: number) => {
    setRipples((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div
      className="relative mx-auto grid min-h-[80vh] place-items-center px-4 py-12 overflow-hidden"
      onClick={handleClick}
    >
      <BeadBackdrop />
      {ripples.map((r) => (
        <Ripple key={r.id} id={r.id} x={r.x} y={r.y} color={r.color} onComplete={removeRipple} />
      ))}
      <div className="w-full max-w-3xl text-center">
        <BeadGarland />
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8 text-3xl font-extrabold text-slate-900"
        >
          Something went wrong
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-3 text-slate-600"
        >
          We are working on this to fix the issue.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-1 text-slate-600"
        >
          Please contact admin if the issue persists.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-6"
        >
          <a href="/">
            <button className="h-11 rounded-2xl px-6 text-base shadow-sm border border-slate-200 bg-white">
              Back to Home
            </button>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default ErrorPageLight;
